import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Nav, Row, Stack, Tab, Table } from 'react-bootstrap'
import {
  approveMasterTerm,
  createMasterTerm,
  createComplianceRule,
  createRulePack,
  deleteMasterTerm,
  deleteComplianceRule,
  deleteProductFamily,
  deleteTermReference,
  fetchMasterTerms,
  fetchProducts,
  fetchRulePacks,
  fetchRulePackWithRules,
  fetchTermReferenceTable,
  importMasterTermToTermReference,
  renameProductFamily,
  retireMasterTerm,
  updateMasterTerm,
  updateTermReference,
  updateRulePackStatus,
  type ComplianceRuleDto,
  type MasterTermDto,
  type ProductDto,
  type RulePackDto,
  type TermReferenceRowDto,
  type UpsertMasterTermRequest,
} from '../services/api'
import { useAuth } from '../context/AuthContext'
import BrandKeywordWhitelist from '../features/pim/BrandKeywordWhitelist'
import './MimSetupV2.css'

type SetupTab = 'master-data' | 'families' | 'attributes' | 'rules' | 'brand-whitelist'
type CompareMode = 'Exact' | 'Pattern' | 'Semantic'
type ComplianceRuleAction = 'CreateFinding' | 'NeedsReview' | 'BlockApproval'
type RuleFormState = {
  phrase: string
  actionIfFound: ComplianceRuleAction
  severity: ComplianceRuleDto['severity']
  key: string
  type: ComplianceRuleDto['type']
  parametersJson: string
  clauses: string
}

type MasterDataFormState = {
  canonicalTerm: string
  category: string
  dataType: string
  aliasesCsv: string
  description: string
}

const EMPTY_FORM: MasterDataFormState = {
  canonicalTerm: '',
  category: 'Technical',
  dataType: 'Text',
  aliasesCsv: '',
  description: '',
}

const EMPTY_RULE_FORM: RuleFormState = {
  phrase: '',
  actionIfFound: 'CreateFinding',
  severity: 'High',
  key: '',
  type: 'RestrictedTerm',
  parametersJson: '',
  clauses: '',
}

const dataGroups = ['Technical', 'Marketing', 'Metadata', 'Downloads', 'Dimensions']
const dataTypes = ['Text', 'Number', 'Boolean', 'Url', 'Date']
const complianceRuleActions: Array<{ value: ComplianceRuleAction; label: string; help: string }> = [
  { value: 'CreateFinding', label: 'Create finding', help: 'Open a compliance finding for review.' },
  { value: 'NeedsReview', label: 'Needs review', help: 'Flag the wording as requiring a manual decision.' },
  { value: 'BlockApproval', label: 'Block approval', help: 'Mark the issue as blocking until resolved.' },
]

const SETUP_TABS: SetupTab[] = ['master-data', 'families', 'attributes', 'rules', 'brand-whitelist']

function parseSetupTab(value: string | null): SetupTab | null {
  if (value && SETUP_TABS.includes(value as SetupTab)) return value as SetupTab
  return null
}

export default function MimSetupV2() {
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const tabFromUrl = parseSetupTab(searchParams.get('tab'))
  const [activeTab, setActiveTab] = useState<SetupTab>(tabFromUrl ?? 'master-data')

  useEffect(() => {
    const tab = parseSetupTab(searchParams.get('tab'))
    if (tab) setActiveTab(tab)
  }, [searchParams])

  const selectSetupTab = (key: SetupTab) => {
    setActiveTab(key)
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('ui')
        next.set('tab', key)
        return next
      },
      { replace: true },
    )
  }

  const [rows, setRows] = useState<MasterTermDto[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [form, setForm] = useState<MasterDataFormState>(EMPTY_FORM)
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [products, setProducts] = useState<ProductDto[]>([])
  const [allFamilyAttributes, setAllFamilyAttributes] = useState<TermReferenceRowDto[]>([])
  const [familyAttributes, setFamilyAttributes] = useState<TermReferenceRowDto[]>([])
  const [selectedFamily, setSelectedFamily] = useState('')
  const [familyNameDraft, setFamilyNameDraft] = useState('')
  const [attributeSearch, setAttributeSearch] = useState('')
  const [showAttributeModal, setShowAttributeModal] = useState(false)
  const [selectedMasterTermIds, setSelectedMasterTermIds] = useState<string[]>([])
  const [familyLoading, setFamilyLoading] = useState(false)
  const [familySaving, setFamilySaving] = useState(false)
  const [familyMessage, setFamilyMessage] = useState('')
  const [familyError, setFamilyError] = useState('')
  const [selectedCompareAttributeId, setSelectedCompareAttributeId] = useState<string | null>(null)
  const [compareMode, setCompareMode] = useState<CompareMode>('Exact')
  const [extractionValueShape, setExtractionValueShape] = useState('')
  const [extractionScope, setExtractionScope] = useState('Product')
  const [extractionVariantScoped, setExtractionVariantScoped] = useState(false)
  const [extractionMarketingMaxLength, setExtractionMarketingMaxLength] = useState(200)
  const [extractionCanonicalUnit, setExtractionCanonicalUnit] = useState('')
  const [compareUseAiSecondOpinion, setCompareUseAiSecondOpinion] = useState(false)
  const [compareSaving, setCompareSaving] = useState(false)
  const [compareMessage, setCompareMessage] = useState('')
  const [compareError, setCompareError] = useState('')
  const [rulePacks, setRulePacks] = useState<RulePackDto[]>([])
  const [selectedRulePackId, setSelectedRulePackId] = useState('')
  const [selectedRulePack, setSelectedRulePack] = useState<RulePackDto | null>(null)
  const [complianceRules, setComplianceRules] = useState<ComplianceRuleDto[]>([])
  const [rulePackForm, setRulePackForm] = useState({ name: '', version: '1.0.0', description: '' })
  const [ruleForm, setRuleForm] = useState<RuleFormState>(EMPTY_RULE_FORM)
  const [rulesLoading, setRulesLoading] = useState(false)
  const [rulesSaving, setRulesSaving] = useState(false)
  const [rulesMessage, setRulesMessage] = useState('')
  const [rulesError, setRulesError] = useState('')

  const canManageMasterData = useMemo(() => {
    const roles = (user?.functionalRoles ?? []).map((r: string) => r.toLowerCase().replace(/\s+/g, ''))
    const businessLevel = ((user as { businessLevel?: string })?.businessLevel ?? '').toLowerCase()
    return businessLevel === 'masteradmin' || roles.includes('masterdata') || roles.includes('publisher') || roles.includes('usermanagement')
  }, [user])

  const canApproveMasterData = useMemo(() => {
    const roles = (user?.functionalRoles ?? []).map((r: string) => r.toLowerCase().replace(/\s+/g, ''))
    const businessLevel = ((user as { businessLevel?: string })?.businessLevel ?? '').toLowerCase()
    return businessLevel === 'masteradmin' || roles.includes('publisher')
  }, [user])

  const selectedRow = useMemo(() => rows.find((row) => row.id === selectedId) ?? null, [rows, selectedId])
  const selectedCompareAttribute = useMemo(
    () => familyAttributes.find((attribute) => attribute.canonicalAttributeId === selectedCompareAttributeId) ?? null,
    [familyAttributes, selectedCompareAttributeId],
  )
  const canEditSelectedRulePack = selectedRulePack?.status === 'Draft'

  const familyOptions = useMemo(() => {
    const names = new Set<string>()
    for (const product of products) {
      const family = product.family?.trim()
      if (family) names.add(family)
    }
    for (const attribute of allFamilyAttributes) {
      const family = attribute.productFamily?.trim()
      if (family && family !== '*') names.add(family)
    }
    if (selectedFamily.trim() && selectedFamily !== '*') names.add(selectedFamily.trim())
    return Array.from(names).sort((a, b) => a.localeCompare(b))
  }, [allFamilyAttributes, products, selectedFamily])

  const selectedFamilyProductCount = useMemo(() => {
    if (!selectedFamily.trim()) return 0
    return products.filter((product) => product.family?.trim() === selectedFamily.trim()).length
  }, [products, selectedFamily])

  const selectedFamilyLabel = selectedFamily.trim() || 'Select a family'

  const availableMasterData = useMemo(() => {
    const query = attributeSearch.trim().toLowerCase()
    const assigned = new Set(familyAttributes.map((attribute) => attribute.canonicalTerm.toLowerCase()))
    return rows
      .filter((row) => row.status === 'Approved')
      .filter((row) => !assigned.has(row.canonicalTerm.toLowerCase()))
      .filter((row) => {
        if (!query) return true
        const blob = `${row.canonicalTerm} ${row.category} ${row.aliases.map((alias) => alias.alias).join(' ')}`.toLowerCase()
        return blob.includes(query)
      })
  }, [attributeSearch, familyAttributes, rows])

  const chooseCompareAttribute = useCallback((attribute: TermReferenceRowDto) => {
    setSelectedCompareAttributeId(attribute.canonicalAttributeId)
    setCompareMode(attribute.compareMode)
    setCompareUseAiSecondOpinion(attribute.useAiSecondOpinion)
    setExtractionValueShape(attribute.valueShape ?? '')
    setExtractionScope(attribute.extractionScope ?? 'Product')
    setExtractionVariantScoped(attribute.variantScoped ?? false)
    setExtractionMarketingMaxLength(attribute.marketingMaxLength ?? 200)
    setExtractionCanonicalUnit(attribute.canonicalUnit ?? '')
    setCompareMessage('')
    setCompareError('')
  }, [])

  const populateFormFromRow = useCallback((row: MasterTermDto) => {
    setSelectedId(row.id)
    setForm({
      canonicalTerm: row.canonicalTerm,
      category: row.category || 'Technical',
      dataType: row.dataType || 'Text',
      aliasesCsv: row.aliases.map((alias) => alias.alias).join(', '),
      description: row.description ?? '',
    })
    setMessage('')
    setError('')
  }, [])

  const loadMasterData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const data = await fetchMasterTerms({
        search: search.trim() || undefined,
        category: categoryFilter || undefined,
      })
      setRows(data)
    } catch (err: any) {
      setError(err.response?.data ?? err.message ?? 'Failed to load master data.')
    } finally {
      setLoading(false)
    }
  }, [categoryFilter, search])

  useEffect(() => {
    loadMasterData()
  }, [loadMasterData])

  const loadFamilyBasics = useCallback(async () => {
    setFamilyLoading(true)
    setFamilyError('')
    try {
      const [productRows, termRows] = await Promise.all([
        fetchProducts(),
        fetchTermReferenceTable(),
      ])
      setProducts(productRows)
      setAllFamilyAttributes(termRows)
      setSelectedFamily((current) => {
        if (current.trim()) return current
        const familyNames = [
          ...productRows.map((product) => product.family?.trim()),
          ...termRows.map((row) => row.productFamily?.trim()).filter((family) => family && family !== '*'),
        ]
        const firstFamily = familyNames
          .filter((family): family is string => !!family)
          .sort((a, b) => a.localeCompare(b))[0]
        return firstFamily ?? ''
      })
    } catch (err: any) {
      setFamilyError(err.response?.data ?? err.message ?? 'Failed to load product families.')
    } finally {
      setFamilyLoading(false)
    }
  }, [])

  useEffect(() => {
    loadFamilyBasics()
  }, [loadFamilyBasics])

  const loadFamilyAttributes = useCallback(async () => {
    const family = selectedFamily.trim()
    if (!family) {
      setFamilyAttributes([])
      setFamilyNameDraft('')
      return
    }

    setFamilyLoading(true)
    setFamilyError('')
    try {
      const data = await fetchTermReferenceTable({ productFamily: family })
      setFamilyAttributes(data)
      setFamilyNameDraft(family)
    } catch (err: any) {
      setFamilyError(err.response?.data ?? err.message ?? 'Failed to load family attributes.')
    } finally {
      setFamilyLoading(false)
    }
  }, [selectedFamily])

  useEffect(() => {
    loadFamilyAttributes()
  }, [loadFamilyAttributes])

  useEffect(() => {
    if (selectedCompareAttributeId && familyAttributes.some((attribute) => attribute.canonicalAttributeId === selectedCompareAttributeId))
      return

    if (familyAttributes.length > 0) {
      chooseCompareAttribute(familyAttributes[0])
    } else {
      setSelectedCompareAttributeId(null)
    }
  }, [chooseCompareAttribute, familyAttributes, selectedCompareAttributeId])

  useEffect(() => {
    if (!selectedId) return
    const latest = rows.find((row) => row.id === selectedId)
    if (latest) populateFormFromRow(latest)
    else {
      setSelectedId(null)
      setForm(EMPTY_FORM)
    }
  }, [populateFormFromRow, rows, selectedId])

  const loadRulePacks = useCallback(async () => {
    setRulesLoading(true)
    setRulesError('')
    try {
      const packs = await fetchRulePacks()
      setRulePacks(packs)
      setSelectedRulePackId((current) => current || packs.find((pack) => pack.status === 'Approved')?.id || packs[0]?.id || '')
    } catch (err: any) {
      setRulesError(err.response?.data ?? err.message ?? 'Failed to load compliance rule packs.')
    } finally {
      setRulesLoading(false)
    }
  }, [])

  useEffect(() => {
    loadRulePacks()
  }, [loadRulePacks])

  const loadSelectedRulePack = useCallback(async () => {
    if (!selectedRulePackId) {
      setSelectedRulePack(null)
      setComplianceRules([])
      return
    }

    setRulesLoading(true)
    setRulesError('')
    try {
      const data = await fetchRulePackWithRules(selectedRulePackId)
      setSelectedRulePack(data.pack)
      setComplianceRules(data.rules)
    } catch (err: any) {
      setRulesError(err.response?.data ?? err.message ?? 'Failed to load compliance rules.')
    } finally {
      setRulesLoading(false)
    }
  }, [selectedRulePackId])

  useEffect(() => {
    loadSelectedRulePack()
  }, [loadSelectedRulePack])

  const buildRequest = (): UpsertMasterTermRequest => {
    const aliases = form.aliasesCsv
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean)
      .map((value) => ({ alias: value }))

    return {
      canonicalTerm: form.canonicalTerm.trim(),
      category: form.category.trim(),
      dataType: form.dataType.trim(),
      description: form.description.trim() || undefined,
      aliases: aliases.length > 0 ? aliases : undefined,
    }
  }

  const startNewAttribute = () => {
    setSelectedId(null)
    setForm(EMPTY_FORM)
    setMessage('')
    setError('')
  }

  const saveAttribute = async (event: FormEvent) => {
    event.preventDefault()
    setMessage('')
    setError('')

    if (!canManageMasterData) {
      setError('You need Master Data, Publisher, User Management, or Master Admin access to manage master data.')
      return
    }

    if (!form.canonicalTerm.trim()) {
      setError('Attribute name is required.')
      return
    }

    setSaving(true)
    try {
      if (selectedId) {
        await updateMasterTerm(selectedId, buildRequest())
        setMessage('Master-data attribute updated.')
      } else {
        const created = await createMasterTerm(buildRequest())
        setSelectedId(created.id)
        setMessage('Draft master-data attribute created.')
      }
      await loadMasterData()
    } catch (err: any) {
      setError(err.response?.data ?? err.message ?? 'Failed to save master-data attribute.')
    } finally {
      setSaving(false)
    }
  }

  const deleteAttribute = async () => {
    if (!selectedId || !selectedRow) return
    if (!canManageMasterData) {
      setError('You need Master Data, Publisher, User Management, or Master Admin access to manage master data.')
      return
    }
    if (!confirm(`Delete "${selectedRow.canonicalTerm}"?`)) return

    setSaving(true)
    setMessage('')
    setError('')
    try {
      await deleteMasterTerm(selectedId)
      setSelectedId(null)
      setForm(EMPTY_FORM)
      setMessage('Master-data attribute deleted.')
      await loadMasterData()
    } catch (err: any) {
      setError(err.response?.data ?? err.message ?? 'Failed to delete master-data attribute.')
    } finally {
      setSaving(false)
    }
  }

  const approveAttribute = async () => {
    if (!selectedId || !selectedRow) return
    setSaving(true)
    setMessage('')
    setError('')
    try {
      await approveMasterTerm(selectedId)
      setMessage(`"${selectedRow.canonicalTerm}" is approved and can be used in families and checks.`)
      await loadMasterData()
    } catch (err: any) {
      setError(err.response?.data ?? err.message ?? 'Failed to approve attribute.')
    } finally {
      setSaving(false)
    }
  }

  const retireAttribute = async () => {
    if (!selectedId || !selectedRow) return
    if (!confirm(`Retire "${selectedRow.canonicalTerm}"? It will no longer be available for new family assignments.`)) return
    setSaving(true)
    setMessage('')
    setError('')
    try {
      await retireMasterTerm(selectedId)
      setMessage(`"${selectedRow.canonicalTerm}" has been retired.`)
      await loadMasterData()
    } catch (err: any) {
      setError(err.response?.data ?? err.message ?? 'Failed to retire attribute.')
    } finally {
      setSaving(false)
    }
  }

  const deleteFamily = async (family: string) => {
    const productCount = products.filter((product) => product.family?.trim() === family).length
    if (productCount > 0) {
      setFamilyError(`Cannot delete "${family}" while it still has ${productCount} product(s).`)
      return
    }
    if (!confirm(`Delete the empty product family "${family}" and its attribute links?`)) return
    setFamilySaving(true)
    setFamilyMessage('')
    setFamilyError('')
    try {
      await deleteProductFamily(family)
      if (selectedFamily === family) startNewFamily()
      await loadFamilyBasics()
      setFamilyMessage(`Product family "${family}" deleted.`)
    } catch (err: any) {
      setFamilyError(err.response?.data ?? err.message ?? 'Failed to delete product family.')
    } finally {
      setFamilySaving(false)
    }
  }

  const chooseFamily = (family: string) => {
    setSelectedFamily(family)
    setFamilyNameDraft(family)
    setSelectedMasterTermIds([])
    setShowAttributeModal(false)
    setFamilyMessage('')
    setFamilyError('')
  }

  const startNewFamily = () => {
    setSelectedFamily('')
    setFamilyNameDraft('')
    setFamilyAttributes([])
    setSelectedMasterTermIds([])
    setShowAttributeModal(false)
    setFamilyMessage('')
    setFamilyError('')
  }

  const useNewFamily = () => {
    const family = familyNameDraft.trim()
    if (!family) return
    chooseFamily(family)
    setFamilyMessage('Family started. Add master-data attributes to save it.')
  }

  const saveFamilyName = async () => {
    const current = selectedFamily.trim()
    const next = familyNameDraft.trim()
    if (!next) {
      setFamilyError('Family name is required.')
      return
    }
    if (!canManageMasterData) {
      setFamilyError('You need Master Data, Publisher, User Management, or Master Admin access to manage families.')
      return
    }
    if (!current) {
      chooseFamily(next)
      setFamilyMessage('Family started. Add master-data attributes to save it.')
      return
    }
    if (current.toLowerCase() === next.toLowerCase()) {
      setFamilyMessage('Family name unchanged.')
      return
    }

    setFamilySaving(true)
    setFamilyMessage('')
    setFamilyError('')
    try {
      const result = await renameProductFamily({ currentName: current, newName: next, actor: 'mim-setup-v2' })
      setSelectedFamily(next)
      setFamilyMessage(result.message)
      await Promise.all([loadFamilyBasics(), loadFamilyAttributes()])
    } catch (err: any) {
      setFamilyError(err.response?.data ?? err.message ?? 'Failed to rename family.')
    } finally {
      setFamilySaving(false)
    }
  }

  const toggleSelectedMasterTerm = (id: string) => {
    setSelectedMasterTermIds((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
    )
  }

  const openAttributeModal = () => {
    setAttributeSearch('')
    setSelectedMasterTermIds([])
    setShowAttributeModal(true)
  }

  const addSelectedAttributesToFamily = async () => {
    const family = selectedFamily.trim()
    if (!family) {
      setFamilyError('Choose or create a family first.')
      return
    }
    if (!canManageMasterData) {
      setFamilyError('You need Master Data, Publisher, User Management, or Master Admin access to manage families.')
      return
    }
    if (selectedMasterTermIds.length === 0) {
      setFamilyError('Select at least one master-data attribute to add.')
      return
    }

    setFamilySaving(true)
    setFamilyMessage('')
    setFamilyError('')
    try {
      const selectedTerms = rows.filter((term) => selectedMasterTermIds.includes(term.id))
      for (const term of selectedTerms) {
        await importMasterTermToTermReference({
          masterTermId: term.id,
          productFamily: family,
          category: term.category,
          includeTranslationsAsAliases: true,
          isCoreTerm: true,
          compareMode: 'Exact',
          useAiSecondOpinion: false,
          applyScope: true,
          actor: 'mim-setup-v2',
        })
      }
      setShowAttributeModal(false)
      setSelectedMasterTermIds([])
      setFamilyMessage(`Added ${selectedTerms.length} attribute${selectedTerms.length === 1 ? '' : 's'} to ${family}.`)
      await Promise.all([loadFamilyBasics(), loadFamilyAttributes()])
    } catch (err: any) {
      setFamilyError(err.response?.data ?? err.message ?? 'Failed to add attributes to family.')
    } finally {
      setFamilySaving(false)
    }
  }

  const removeAttributeFromFamily = async (attribute: TermReferenceRowDto) => {
    if (!canManageMasterData) return
    if (!confirm(`Remove "${attribute.canonicalTerm}" from ${selectedFamilyLabel}?`)) return

    setFamilySaving(true)
    setFamilyMessage('')
    setFamilyError('')
    try {
      await deleteTermReference(attribute.canonicalAttributeId)
      setFamilyMessage(`Removed ${attribute.canonicalTerm} from ${selectedFamilyLabel}.`)
      await Promise.all([loadFamilyBasics(), loadFamilyAttributes()])
    } catch (err: any) {
      setFamilyError(err.response?.data ?? err.message ?? 'Failed to remove attribute from family.')
    } finally {
      setFamilySaving(false)
    }
  }

  const applyComparePreset = (mode: CompareMode) => {
    setCompareMode(mode)
    setCompareUseAiSecondOpinion(mode === 'Semantic')
  }

  const saveCompareSettings = async () => {
    if (!selectedCompareAttribute) {
      setCompareError('Choose an attribute first.')
      return
    }
    if (!canManageMasterData) {
      setCompareError('You need Master Data, Publisher, User Management, or Master Admin access to manage comparison settings.')
      return
    }

    setCompareSaving(true)
    setCompareMessage('')
    setCompareError('')
    try {
      await updateTermReference(selectedCompareAttribute.canonicalAttributeId, {
        compareMode,
        useAiSecondOpinion: compareMode === 'Semantic' ? true : compareUseAiSecondOpinion,
        valueShape: extractionValueShape || undefined,
        extractionScope: extractionScope || undefined,
        variantScoped: extractionVariantScoped,
        marketingMaxLength: extractionMarketingMaxLength,
        canonicalUnit: extractionCanonicalUnit || undefined,
        actor: 'mim-setup-v2',
      })
      const refreshed = await fetchTermReferenceTable({ productFamily: selectedFamily.trim() })
      setFamilyAttributes(refreshed)
      const updated = refreshed.find(
        (row) => row.canonicalAttributeId === selectedCompareAttribute.canonicalAttributeId,
      )
      if (updated) chooseCompareAttribute(updated)
      setCompareMessage('Comparison settings saved.')
    } catch (err: any) {
      setCompareError(err.response?.data ?? err.message ?? 'Failed to save comparison settings.')
    } finally {
      setCompareSaving(false)
    }
  }

  const selectRulePack = (id: string) => {
    setSelectedRulePackId(id)
    setRulesMessage('')
    setRulesError('')
  }

  const normalizeRuleKey = (phrase: string) => {
    const normalized = phrase
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
    return `restricted_term_${normalized || Date.now()}`
  }

  const getRuleActionLabel = (action: string | undefined) => (
    complianceRuleActions.find((option) => option.value === action)?.label ?? 'Create finding'
  )

  const getRuleAction = (rule: ComplianceRuleDto) => {
    if (!rule.parametersJson?.trim()) return 'CreateFinding'
    try {
      const parameters = JSON.parse(rule.parametersJson)
      return typeof parameters.actionIfFound === 'string' ? parameters.actionIfFound : 'CreateFinding'
    } catch {
      return 'CreateFinding'
    }
  }

  const isSystemComparisonRule = (rule: ComplianceRuleDto) => (
    rule.key === 'field_mismatch' ||
    rule.key === 'download_hash_mismatch' ||
    (rule.type === 'FieldRule' && rule.parametersJson?.includes('"systemRule":true'))
  )

  const visibleComplianceRules = complianceRules.filter((rule) => !isSystemComparisonRule(rule))

  const buildRuleParametersJson = () => {
    const base = ruleForm.parametersJson.trim()
      ? JSON.parse(ruleForm.parametersJson)
      : {}

    if (base === null || Array.isArray(base) || typeof base !== 'object') {
      throw new Error('Advanced parameters must be a JSON object.')
    }

    return JSON.stringify({
      ...base,
      actionIfFound: ruleForm.actionIfFound,
    })
  }

  const saveRulePack = async (event: FormEvent) => {
    event.preventDefault()
    setRulesMessage('')
    setRulesError('')

    if (!canManageMasterData) {
      setRulesError('You need Master Data, Publisher, User Management, or Master Admin access to manage compliance rules.')
      return
    }
    if (!rulePackForm.name.trim() || !rulePackForm.version.trim()) {
      setRulesError('Rule pack name and version are required.')
      return
    }

    setRulesSaving(true)
    try {
      const created = await createRulePack({
        name: rulePackForm.name.trim(),
        version: rulePackForm.version.trim(),
        description: rulePackForm.description.trim() || undefined,
      })
      setRulePackForm({ name: '', version: '1.0.0', description: '' })
      setRulesMessage('Draft rule pack created.')
      await loadRulePacks()
      setSelectedRulePackId(created.id)
    } catch (err: any) {
      setRulesError(err.response?.data ?? err.message ?? 'Failed to create rule pack.')
    } finally {
      setRulesSaving(false)
    }
  }

  const saveRule = async (event: FormEvent) => {
    event.preventDefault()
    setRulesMessage('')
    setRulesError('')

    if (!selectedRulePackId || !selectedRulePack) {
      setRulesError('Choose a rule pack first.')
      return
    }
    if (!canManageMasterData) {
      setRulesError('You need Master Data, Publisher, User Management, or Master Admin access to manage compliance rules.')
      return
    }
    if (!canEditSelectedRulePack) {
      setRulesError('Only draft rule packs can be edited.')
      return
    }
    if (!ruleForm.phrase.trim()) {
      setRulesError('Word or phrase is required.')
      return
    }
    if (!ruleForm.actionIfFound) {
      setRulesError('Action if found is required.')
      return
    }

    let parametersJson = ''
    try {
      parametersJson = buildRuleParametersJson()
    } catch (err: any) {
      setRulesError(err.message ?? 'Parameters must be valid JSON.')
      return
    }

    setRulesSaving(true)
    try {
      const phrase = ruleForm.phrase.trim()
      const key = ruleForm.key.trim() || normalizeRuleKey(phrase)
      await createComplianceRule(selectedRulePackId, {
        key,
        name: `Restricted term: ${phrase}`,
        type: ruleForm.type,
        pattern: phrase,
        parametersJson,
        severity: ruleForm.severity,
        clauses: ruleForm.clauses.trim() || undefined,
      })
      setRuleForm(EMPTY_RULE_FORM)
      setRulesMessage('Compliance rule added.')
      await loadSelectedRulePack()
    } catch (err: any) {
      setRulesError(err.response?.data ?? err.message ?? 'Failed to add compliance rule.')
    } finally {
      setRulesSaving(false)
    }
  }

  const changeRulePackStatus = async (status: RulePackDto['status']) => {
    if (!selectedRulePackId) return
    if (!canManageMasterData) {
      setRulesError('You need Master Data, Publisher, User Management, or Master Admin access to manage compliance rules.')
      return
    }

    setRulesSaving(true)
    setRulesMessage('')
    setRulesError('')
    try {
      await updateRulePackStatus(selectedRulePackId, status)
      setRulesMessage(status === 'Approved' ? 'Rule pack approved for compliance checks.' : 'Rule pack retired.')
      await Promise.all([loadRulePacks(), loadSelectedRulePack()])
    } catch (err: any) {
      setRulesError(err.response?.data ?? err.message ?? 'Failed to update rule pack status.')
    } finally {
      setRulesSaving(false)
    }
  }

  const removeRule = async (rule: ComplianceRuleDto) => {
    if (!canEditSelectedRulePack || !canManageMasterData) return
    if (!confirm(`Delete "${rule.name}"?`)) return

    setRulesSaving(true)
    setRulesMessage('')
    setRulesError('')
    try {
      await deleteComplianceRule(rule.id)
      setRulesMessage('Compliance rule deleted.')
      await loadSelectedRulePack()
    } catch (err: any) {
      setRulesError(err.response?.data ?? err.message ?? 'Failed to delete compliance rule.')
    } finally {
      setRulesSaving(false)
    }
  }

  const statusVariant = (status: string) => {
    if (status === 'Approved') return 'success'
    if (status === 'Retired') return 'warning'
    return 'secondary'
  }

  return (
    <Container className="mim-setup-v2 pb-4">
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
        <div>
          <h1 className="h3 mb-1">MIM Setup</h1>
          <p className="text-muted mb-0">
            A step-by-step workspace for defining master data first, then using it in product-family checks.
          </p>
        </div>
      </div>

      <Tab.Container activeKey={activeTab} onSelect={(key) => {
        if (key && SETUP_TABS.includes(key as SetupTab)) selectSetupTab(key as SetupTab)
      }}>
        <Nav variant="tabs" className="mb-3" data-tour="mim-setup-tabs">
          <Nav.Item>
            <Nav.Link eventKey="master-data">1. Master data</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="families">2. Families</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="attributes">3. Compared attributes</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="rules">4. Compliance rules</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="brand-whitelist">5. Brand whitelist</Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="master-data">
            <Row className="g-3">
              <Col lg={4}>
                <Card className="h-100" data-tour="mim-master-data-form">
                  <Card.Body>
                    <div className="mb-3">
                      <h2 className="h5 mb-1">{selectedId ? 'Edit attribute' : 'Create an attribute'}</h2>
                      <p className="text-muted small mb-0">
                        {canManageMasterData
                          ? 'Click a row to edit it here, or create a new reusable field.'
                          : 'You can view master data. Ask an admin for access to edit it.'}
                      </p>
                    </div>

                    <Alert variant="light" className="py-2 small mb-3 border">
                      <strong>Draft</strong> — proposed, editable, not yet used in compliance checks.
                      {' '}<strong>Approved</strong> — ready to add to families and run checks.
                      {' '}<strong>Retired</strong> — hidden from new use.
                    </Alert>

                    {selectedRow && (
                      <div className="mb-3">
                        Status: <Badge bg={statusVariant(selectedRow.status)}>{selectedRow.status}</Badge>
                      </div>
                    )}

                    {!canManageMasterData ? (
                      <div className="border rounded p-3 text-muted">
                        Master data is read-only for your account.
                      </div>
                    ) : (
                    <Form onSubmit={saveAttribute}>
                      <Stack gap={3}>
                        <div>
                          <Form.Label>Attribute name</Form.Label>
                          <Form.Control
                            value={form.canonicalTerm}
                            onChange={(event) => setForm({ ...form, canonicalTerm: event.target.value })}
                            placeholder="Thickness"
                          />
                        </div>

                        <Row className="g-2">
                          <Col md={7}>
                            <Form.Label>Master data group</Form.Label>
                            <Form.Select
                              value={form.category}
                              onChange={(event) => setForm({ ...form, category: event.target.value })}
                            >
                              {dataGroups.map((group) => (
                                <option key={group} value={group}>{group}</option>
                              ))}
                            </Form.Select>
                          </Col>
                          <Col md={5}>
                            <Form.Label>Data type</Form.Label>
                            <Form.Select
                              value={form.dataType}
                              onChange={(event) => setForm({ ...form, dataType: event.target.value })}
                            >
                              {dataTypes.map((type) => (
                                <option key={type} value={type}>{type}</option>
                              ))}
                            </Form.Select>
                          </Col>
                        </Row>

                        <div>
                          <Form.Label>Alternative names</Form.Label>
                          <Form.Control
                            value={form.aliasesCsv}
                            onChange={(event) => setForm({ ...form, aliasesCsv: event.target.value })}
                            placeholder="Thicknesses, Board thickness"
                          />
                          <Form.Text>Comma-separated; translations can come later.</Form.Text>
                        </div>

                        <div>
                          <Form.Label>Notes</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            value={form.description}
                            onChange={(event) => setForm({ ...form, description: event.target.value })}
                            placeholder="What should people use this field for?"
                          />
                        </div>

                        <div className="d-flex flex-wrap gap-2">
                          <Button type="submit" disabled={saving}>
                            {saving ? 'Saving...' : selectedId ? 'Save changes' : 'Create draft attribute'}
                          </Button>
                          {selectedId && canApproveMasterData && selectedRow?.status !== 'Approved' && (
                            <Button type="button" variant="success" onClick={approveAttribute} disabled={saving}>
                              Approve
                            </Button>
                          )}
                          {selectedId && canApproveMasterData && selectedRow?.status !== 'Retired' && (
                            <Button type="button" variant="outline-warning" onClick={retireAttribute} disabled={saving}>
                              Retire
                            </Button>
                          )}
                          {selectedId && (
                            <>
                              <Button type="button" variant="outline-secondary" onClick={startNewAttribute} disabled={saving}>
                                New attribute
                              </Button>
                              <Button type="button" variant="outline-danger" onClick={deleteAttribute} disabled={saving}>
                                Delete
                              </Button>
                            </>
                          )}
                        </div>
                      </Stack>
                    </Form>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={8}>
                <Card data-tour="mim-master-data-library">
                  <Card.Body>
                    <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                      <div>
                        <h2 className="h5 mb-1">Master data library</h2>
                        <p className="text-muted small mb-0">
                          These are the reusable attributes that later feed families and compliance checks.
                        </p>
                      </div>
                      <Button type="button" variant="outline-primary" onClick={loadMasterData} disabled={loading}>
                        Refresh
                      </Button>
                    </div>

                    {message && <Alert variant="success" className="py-2">{message}</Alert>}
                    {error && <Alert variant="danger" className="py-2">{error}</Alert>}

                    <Row className="g-2 mb-3">
                      <Col md={7}>
                        <Form.Label>Search</Form.Label>
                        <Form.Control
                          value={search}
                          onChange={(event) => setSearch(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') loadMasterData()
                          }}
                          placeholder="Search attribute or alternative name"
                        />
                      </Col>
                      <Col md={3}>
                        <Form.Label>Group</Form.Label>
                        <Form.Select value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)}>
                          <option value="">All groups</option>
                          {dataGroups.map((group) => (
                            <option key={group} value={group}>{group}</option>
                          ))}
                        </Form.Select>
                      </Col>
                      <Col md={2} className="d-flex align-items-end">
                        <Button type="button" variant="outline-primary" className="w-100" onClick={loadMasterData}>
                          Apply
                        </Button>
                      </Col>
                    </Row>

                    {loading ? (
                      <div className="text-muted">Loading master data...</div>
                    ) : rows.length === 0 ? (
                      <div className="border rounded p-4 text-center text-muted">
                        No attributes found. Create the first draft on the left.
                      </div>
                    ) : (
                      <Table responsive hover className="align-middle mb-0">
                        <thead>
                          <tr>
                            <th>Attribute</th>
                            <th>Group</th>
                            <th>Type</th>
                            <th>Status</th>
                            <th>Alternative names</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row) => (
                            <tr
                              key={row.id}
                              className={row.id === selectedId ? 'table-primary' : ''}
                              onClick={() => populateFormFromRow(row)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') populateFormFromRow(row)
                              }}
                            >
                              <td>
                                <div className="fw-semibold">{row.canonicalTerm}</div>
                                {row.description && <div className="text-muted mim-setup-help-text">{row.description}</div>}
                              </td>
                              <td>{row.category}</td>
                              <td>{row.dataType}</td>
                              <td><Badge bg={statusVariant(row.status)}>{row.status}</Badge></td>
                              <td className="text-muted">
                                {row.aliases.length > 0 ? row.aliases.slice(0, 3).map((alias) => alias.alias).join(', ') : '-'}
                                {row.aliases.length > 3 ? ` +${row.aliases.length - 3} more` : ''}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab.Pane>

          <Tab.Pane eventKey="families">
            <Row className="g-3">
              <Col lg={4}>
                <Card className="h-100" data-tour="mim-family-name">
                  <Card.Body>
                    <div className="mb-3">
                      <h2 className="h5 mb-1">Product families</h2>
                      <p className="text-muted small mb-0">
                        Add or edit a family, then attach master-data attributes to it.
                      </p>
                    </div>

                    <Stack gap={3}>
                      <div>
                        <div className="d-flex justify-content-between align-items-center gap-2">
                          <Form.Label className="mb-0">Family name</Form.Label>
                          {selectedFamily && canManageMasterData && (
                            <Button type="button" variant="link" className="p-0" onClick={startNewFamily}>
                              New family
                            </Button>
                          )}
                        </div>
                        <div className="d-flex gap-2">
                          <Form.Control
                            value={familyNameDraft}
                            onChange={(event) => setFamilyNameDraft(event.target.value)}
                            placeholder="Floor Insulation boards"
                            disabled={!canManageMasterData}
                          />
                          <Button type="button" variant="outline-primary" onClick={selectedFamily ? saveFamilyName : useNewFamily} disabled={!canManageMasterData || familySaving}>
                            {selectedFamily ? 'Save' : 'Add'}
                          </Button>
                        </div>
                        <Form.Text>{selectedFamily ? 'Rename the selected family.' : 'Create a family, then add attributes from master data.'}</Form.Text>
                      </div>

                      <div className="mim-family-list" data-tour="mim-family-list">
                        {familyLoading && familyOptions.length === 0 ? (
                          <div className="text-muted">Loading families...</div>
                        ) : familyOptions.length === 0 ? (
                          <div className="border rounded p-3 text-muted">No product families found yet.</div>
                        ) : (
                          familyOptions.map((family) => {
                            const productCount = products.filter((product) => product.family?.trim() === family).length
                            return (
                              <div
                                key={family}
                                className={`mim-family-list-item ${family === selectedFamily ? 'active' : ''}`}
                              >
                                <button type="button" className="mim-family-list-item-main" onClick={() => chooseFamily(family)}>
                                  <span>{family}</span>
                                  <span className="text-muted">{productCount} product{productCount === 1 ? '' : 's'}</span>
                                </button>
                                {canManageMasterData && productCount === 0 && (
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline-danger"
                                    className="mim-family-delete-btn"
                                    disabled={familySaving}
                                    onClick={(event) => {
                                      event.stopPropagation()
                                      void deleteFamily(family)
                                    }}
                                  >
                                    Delete
                                  </Button>
                                )}
                              </div>
                            )
                          })
                        )}
                      </div>
                    </Stack>
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={8}>
                <Card>
                  <Card.Body>
                    <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                      <div>
                        <h2 className="h5 mb-1">{selectedFamilyLabel}</h2>
                        <p className="text-muted small mb-0">
                          {selectedFamily.trim()
                            ? `${selectedFamilyProductCount} product${selectedFamilyProductCount === 1 ? '' : 's'} and ${familyAttributes.length} attribute${familyAttributes.length === 1 ? '' : 's'}.`
                            : 'Choose a family to continue.'}
                        </p>
                      </div>
                      <Button type="button" variant="outline-primary" onClick={loadFamilyBasics} disabled={familyLoading}>
                        Refresh
                      </Button>
                    </div>

                    {familyMessage && <Alert variant="success" className="py-2">{familyMessage}</Alert>}
                    {familyError && <Alert variant="danger" className="py-2">{familyError}</Alert>}

                    {!selectedFamily.trim() ? (
                      <div className="border rounded p-4 text-center text-muted">
                        Select a product family on the left.
                      </div>
                    ) : (
                      <>
                        <div className="mb-3">
                          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                            <h3 className="h6 mb-0">Family attributes</h3>
                            {canManageMasterData && (
                              <Button type="button" onClick={openAttributeModal} disabled={familySaving} data-tour="mim-family-add-attribute">
                                Add attribute
                              </Button>
                            )}
                          </div>
                          {familyAttributes.length === 0 ? (
                            <div className="border rounded p-3 text-muted">
                              No attributes added yet. Use Add attribute to choose from master data.
                            </div>
                          ) : (
                            <Table responsive hover className="align-middle mb-0">
                              <thead>
                                <tr>
                                  <th>Attribute</th>
                                  <th>Group</th>
                                  <th>Compare</th>
                                  <th></th>
                                </tr>
                              </thead>
                              <tbody>
                                {familyAttributes.map((attribute) => (
                                  <tr key={attribute.canonicalAttributeId}>
                                    <td>
                                      <div className="fw-semibold">{attribute.canonicalTerm}</div>
                                      {attribute.alternatives.length > 0 && (
                                        <div className="text-muted mim-setup-help-text">{attribute.alternatives.slice(0, 3).join(', ')}</div>
                                      )}
                                    </td>
                                    <td>{attribute.category}</td>
                                    <td>{attribute.compareMode}</td>
                                    <td className="text-end">
                                      {canManageMasterData && (
                                        <Button
                                          type="button"
                                          size="sm"
                                          variant="outline-danger"
                                          onClick={() => removeAttributeFromFamily(attribute)}
                                          disabled={familySaving}
                                        >
                                          Remove
                                        </Button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          )}
                        </div>
                      </>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab.Pane>

          <Tab.Pane eventKey="attributes">
            <Row className="g-3">
              <Col lg={4}>
                <Card className="h-100" data-tour="mim-compare-family">
                  <Card.Body>
                    <div className="mb-3">
                      <h2 className="h5 mb-1">Product family</h2>
                      <p className="text-muted small mb-0">
                        Pick the family whose attributes you want to compare. Compare mode is set per family attribute
                        (Exact, Pattern, Semantic) so different product lines can use different check styles.
                      </p>
                    </div>

                    <div className="mim-family-list">
                      {familyLoading && familyOptions.length === 0 ? (
                        <div className="text-muted">Loading families...</div>
                      ) : familyOptions.length === 0 ? (
                        <div className="border rounded p-3 text-muted">No product families found yet.</div>
                      ) : (
                        familyOptions.map((family) => {
                          const attributeCount = allFamilyAttributes.filter((attribute) => attribute.productFamily?.trim() === family).length
                          return (
                            <button
                              key={family}
                              type="button"
                              className={`mim-family-list-item ${family === selectedFamily ? 'active' : ''}`}
                              onClick={() => chooseFamily(family)}
                            >
                              <span>{family}</span>
                              <span className="text-muted">{attributeCount} attribute{attributeCount === 1 ? '' : 's'}</span>
                            </button>
                          )
                        })
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={8}>
                <Card>
                  <Card.Body>
                    <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                      <div>
                        <h2 className="h5 mb-1">Compared attributes</h2>
                        <p className="text-muted small mb-0">
                          Choose how each family attribute should be compared during checks.
                        </p>
                      </div>
                      <Button type="button" variant="outline-primary" onClick={loadFamilyAttributes} disabled={familyLoading}>
                        Refresh
                      </Button>
                    </div>

                    {compareMessage && <Alert variant="success" className="py-2">{compareMessage}</Alert>}
                    {compareError && <Alert variant="danger" className="py-2">{compareError}</Alert>}

                    {!selectedFamily.trim() ? (
                      <div className="border rounded p-4 text-center text-muted">
                        Select a product family on the left.
                      </div>
                    ) : familyAttributes.length === 0 ? (
                      <div className="border rounded p-4 text-center text-muted">
                        This family has no attributes yet. Add them on the Families tab first.
                      </div>
                    ) : (
                      <Row className="g-3">
                        <Col md={5}>
                          <div className="mim-attribute-list" data-tour="mim-compare-list">
                            {familyAttributes.map((attribute) => (
                              <button
                                key={attribute.canonicalAttributeId}
                                type="button"
                                className={`mim-attribute-list-item ${attribute.canonicalAttributeId === selectedCompareAttributeId ? 'active' : ''}`}
                                onClick={() => chooseCompareAttribute(attribute)}
                              >
                                <span>
                                  <span className="fw-semibold">{attribute.canonicalTerm}</span>
                                  <span className="text-muted d-block mim-setup-help-text">{attribute.category}</span>
                                </span>
                                <Badge bg={attribute.compareMode === 'Semantic' ? 'info' : attribute.compareMode === 'Pattern' ? 'warning' : 'secondary'}>
                                  {attribute.compareMode}
                                </Badge>
                              </button>
                            ))}
                          </div>
                        </Col>

                        <Col md={7}>
                          {selectedCompareAttribute ? (
                            <div className="border rounded p-3" data-tour="mim-compare-settings">
                              <div className="mb-3">
                                <h3 className="h6 mb-1">{selectedCompareAttribute.canonicalTerm}</h3>
                                <div className="text-muted mim-setup-help-text">
                                  {selectedCompareAttribute.category} · {selectedCompareAttribute.alternatives.length} alternative name{selectedCompareAttribute.alternatives.length === 1 ? '' : 's'}
                                </div>
                              </div>

                              <Stack gap={3}>
                                <div>
                                  <Form.Label>Compare mode</Form.Label>
                                  <div className="mim-compare-options">
                                    <button
                                      type="button"
                                      className={`mim-compare-option ${compareMode === 'Exact' ? 'active' : ''}`}
                                      onClick={() => applyComparePreset('Exact')}
                                    >
                                      <span className="fw-semibold">Exact</span>
                                      <span className="text-muted mim-setup-help-text">Numbers, dimensions, short values.</span>
                                    </button>
                                    <button
                                      type="button"
                                      className={`mim-compare-option ${compareMode === 'Pattern' ? 'active' : ''}`}
                                      onClick={() => applyComparePreset('Pattern')}
                                    >
                                      <span className="fw-semibold">Pattern</span>
                                      <span className="text-muted mim-setup-help-text">URLs, files, codes, structured text.</span>
                                    </button>
                                    <button
                                      type="button"
                                      className={`mim-compare-option ${compareMode === 'Semantic' ? 'active' : ''}`}
                                      onClick={() => applyComparePreset('Semantic')}
                                    >
                                      <span className="fw-semibold">Semantic</span>
                                      <span className="text-muted mim-setup-help-text">Marketing or descriptive text.</span>
                                    </button>
                                  </div>
                                </div>

                                <Form.Check
                                  type="switch"
                                  id="mim-compare-ai"
                                  label="Use AI"
                                  checked={compareMode === 'Semantic' || compareUseAiSecondOpinion}
                                  onChange={(event) => setCompareUseAiSecondOpinion(event.target.checked)}
                                  disabled={compareMode === 'Semantic'}
                                />

                                <div className="border rounded p-3">
                                  <div className="fw-semibold mb-2">Extraction profile (what good looks like)</div>
                                  <Row className="g-2">
                                    <Col md={6}>
                                      <Form.Label className="small mb-0">Value shape</Form.Label>
                                      <Form.Select size="sm" value={extractionValueShape} onChange={e => setExtractionValueShape(e.target.value)}>
                                        <option value="">Auto (from term name)</option>
                                        <option value="ScalarWithUnit">Scalar with unit</option>
                                        <option value="FireClass">Fire class</option>
                                        <option value="TextShort">Short text</option>
                                        <option value="Boolean">Boolean</option>
                                      </Form.Select>
                                    </Col>
                                    <Col md={6}>
                                      <Form.Label className="small mb-0">Scope</Form.Label>
                                      <Form.Select size="sm" value={extractionScope} onChange={e => setExtractionScope(e.target.value)}>
                                        <option value="Product">Product BES data</option>
                                        <option value="Manufacturer">Manufacturer / company</option>
                                      </Form.Select>
                                    </Col>
                                    <Col md={6}>
                                      <Form.Label className="small mb-0">Canonical unit</Form.Label>
                                      <Form.Control size="sm" value={extractionCanonicalUnit} onChange={e => setExtractionCanonicalUnit(e.target.value)} placeholder="e.g. mm, W/mK" />
                                    </Col>
                                    <Col md={6}>
                                      <Form.Label className="small mb-0">Max marketing length</Form.Label>
                                      <Form.Control size="sm" type="number" value={extractionMarketingMaxLength} onChange={e => setExtractionMarketingMaxLength(Number(e.target.value) || 200)} />
                                    </Col>
                                    <Col md={12}>
                                      <Form.Check
                                        type="switch"
                                        id="mim-variant-scoped"
                                        label="Variant-scoped (e.g. R-value by thickness)"
                                        checked={extractionVariantScoped}
                                        onChange={e => setExtractionVariantScoped(e.target.checked)}
                                      />
                                    </Col>
                                  </Row>
                                </div>

                                {canManageMasterData ? (
                                  <Button type="button" onClick={saveCompareSettings} disabled={compareSaving}>
                                    {compareSaving ? 'Saving...' : 'Save comparison settings'}
                                  </Button>
                                ) : (
                                  <div className="text-muted">Read-only for your account.</div>
                                )}
                              </Stack>
                            </div>
                          ) : (
                            <div className="border rounded p-4 text-center text-muted">
                              Select an attribute to edit comparison settings.
                            </div>
                          )}
                        </Col>
                      </Row>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab.Pane>

          <Tab.Pane eventKey="rules">
            <Row className="g-3">
              <Col lg={4}>
                <Card className="h-100" data-tour="mim-rules-packs">
                  <Card.Body>
                    <div className="mb-3">
                      <h2 className="h5 mb-1">Rule packs</h2>
                      <p className="text-muted small mb-0">
                        A rule pack is the versioned set of compliance checks used when audit jobs run.
                      </p>
                    </div>

                    <Stack gap={3}>
                      {canManageMasterData ? (
                        <Form onSubmit={saveRulePack}>
                          <Stack gap={2}>
                            <div>
                              <Form.Label>Name</Form.Label>
                              <Form.Control
                                value={rulePackForm.name}
                                onChange={(event) => setRulePackForm({ ...rulePackForm, name: event.target.value })}
                                placeholder="MIM compliance rules"
                              />
                            </div>
                            <Row className="g-2">
                              <Col md={5}>
                                <Form.Label>Version</Form.Label>
                                <Form.Control
                                  value={rulePackForm.version}
                                  onChange={(event) => setRulePackForm({ ...rulePackForm, version: event.target.value })}
                                  placeholder="1.0.0"
                                />
                              </Col>
                              <Col md={7}>
                                <Form.Label>Description</Form.Label>
                                <Form.Control
                                  value={rulePackForm.description}
                                  onChange={(event) => setRulePackForm({ ...rulePackForm, description: event.target.value })}
                                  placeholder="Optional"
                                />
                              </Col>
                            </Row>
                            <Button type="submit" disabled={rulesSaving}>
                              {rulesSaving ? 'Saving...' : 'Create draft pack'}
                            </Button>
                          </Stack>
                        </Form>
                      ) : (
                        <div className="border rounded p-3 text-muted">Compliance rules are read-only for your account.</div>
                      )}

                      <div className="mim-family-list">
                        {rulesLoading && rulePacks.length === 0 ? (
                          <div className="text-muted">Loading rule packs...</div>
                        ) : rulePacks.length === 0 ? (
                          <div className="border rounded p-3 text-muted">No rule packs found yet.</div>
                        ) : (
                          rulePacks.map((pack) => (
                            <button
                              key={pack.id}
                              type="button"
                              className={`mim-family-list-item ${pack.id === selectedRulePackId ? 'active' : ''}`}
                              onClick={() => selectRulePack(pack.id)}
                            >
                              <span>
                                <span className="fw-semibold">{pack.name}</span>
                                <span className="text-muted d-block mim-setup-help-text">v{pack.version}</span>
                              </span>
                              <Badge bg={statusVariant(pack.status)}>{pack.status}</Badge>
                            </button>
                          ))
                        )}
                      </div>
                    </Stack>
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={8}>
                <Card data-tour="mim-rules-editor">
                  <Card.Body>
                    <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                      <div>
                        <h2 className="h5 mb-1">{selectedRulePack ? selectedRulePack.name : 'Compliance rules'}</h2>
                        <p className="text-muted small mb-0">
                          {selectedRulePack
                            ? `Version ${selectedRulePack.version} · ${visibleComplianceRules.length} rule${visibleComplianceRules.length === 1 ? '' : 's'}`
                            : 'Choose a rule pack to add or review rules.'}
                        </p>
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        {selectedRulePack?.status === 'Draft' && canManageMasterData && (
                          <Button type="button" variant="success" onClick={() => changeRulePackStatus('Approved')} disabled={rulesSaving}>
                            Approve pack
                          </Button>
                        )}
                        {selectedRulePack && selectedRulePack.status !== 'Retired' && canManageMasterData && (
                          <Button type="button" variant="outline-warning" onClick={() => changeRulePackStatus('Retired')} disabled={rulesSaving}>
                            Retire
                          </Button>
                        )}
                        <Button type="button" variant="outline-primary" onClick={() => {
                          loadRulePacks()
                          loadSelectedRulePack()
                        }} disabled={rulesLoading}>
                          Refresh
                        </Button>
                      </div>
                    </div>

                    {selectedRulePack && (
                      <div className="mb-3">
                        <Badge bg={statusVariant(selectedRulePack.status)} className="me-2">{selectedRulePack.status}</Badge>
                        <span className="text-muted mim-setup-help-text">
                          {selectedRulePack.status === 'Draft'
                            ? 'Draft packs can be edited. Approve when the rules are ready for checks.'
                            : selectedRulePack.status === 'Approved'
                              ? 'Approved packs are available to compliance checks and are read-only here.'
                              : 'Retired packs are kept for history and are not used for new checks.'}
                        </span>
                      </div>
                    )}

                    {rulesMessage && <Alert variant="success" className="py-2">{rulesMessage}</Alert>}
                    {rulesError && <Alert variant="danger" className="py-2">{rulesError}</Alert>}

                    {!selectedRulePack ? (
                      <div className="border rounded p-4 text-center text-muted">
                        Select or create a rule pack on the left.
                      </div>
                    ) : (
                      <Row className="g-3">
                        <Col md={5}>
                          <div className="border rounded p-3">
                            <div className="mb-3">
                              <h3 className="h6 mb-1">Add a rule</h3>
                              <div className="text-muted mim-setup-help-text">
                                Keep rules simple: restricted words, unsupported claims, broken-link checks, or system field rules.
                              </div>
                            </div>

                            {!canEditSelectedRulePack ? (
                              <div className="text-muted">This rule pack is not editable.</div>
                            ) : (
                              <Form onSubmit={saveRule}>
                                <Stack gap={3}>
                                  <div>
                                    <Form.Label>Word or phrase</Form.Label>
                                    <Form.Control
                                      value={ruleForm.phrase}
                                      onChange={(event) => setRuleForm({ ...ruleForm, phrase: event.target.value })}
                                      placeholder="eco-friendly"
                                      disabled={!canManageMasterData}
                                    />
                                    <Form.Text>The exact word or phrase to look for in distributor content.</Form.Text>
                                  </div>

                                  <Row className="g-2">
                                    <Col md={6}>
                                      <Form.Label>Action if found</Form.Label>
                                      <Form.Select
                                        value={ruleForm.actionIfFound}
                                        onChange={(event) => setRuleForm({ ...ruleForm, actionIfFound: event.target.value as ComplianceRuleAction })}
                                        disabled={!canManageMasterData}
                                      >
                                        {complianceRuleActions.map((option) => (
                                          <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                      </Form.Select>
                                      <Form.Text>
                                        {complianceRuleActions.find((option) => option.value === ruleForm.actionIfFound)?.help}
                                      </Form.Text>
                                    </Col>
                                    <Col md={6}>
                                      <Form.Label>Severity</Form.Label>
                                      <Form.Select
                                        value={ruleForm.severity}
                                        onChange={(event) => setRuleForm({ ...ruleForm, severity: event.target.value as ComplianceRuleDto['severity'] })}
                                        disabled={!canManageMasterData}
                                      >
                                        <option value="Critical">Critical</option>
                                        <option value="High">High</option>
                                        <option value="Medium">Medium</option>
                                        <option value="Low">Low</option>
                                        <option value="Info">Info</option>
                                      </Form.Select>
                                    </Col>
                                  </Row>

                                  {canManageMasterData && (
                                    <details className="border rounded p-3">
                                      <summary className="fw-semibold">Advanced details</summary>
                                      <Stack gap={2} className="mt-3">
                                        <Row className="g-2">
                                          <Col md={6}>
                                            <Form.Label>Rule type</Form.Label>
                                            <Form.Select
                                              value={ruleForm.type}
                                              onChange={(event) => setRuleForm({ ...ruleForm, type: event.target.value as ComplianceRuleDto['type'] })}
                                            >
                                              <option value="RestrictedTerm">Restricted term</option>
                                              <option value="UnsupportedClaim">Unsupported claim</option>
                                              <option value="BrokenLink">Broken link</option>
                                              <option value="RequiredClaim">Required reference</option>
                                            </Form.Select>
                                          </Col>
                                          <Col md={6}>
                                            <Form.Label>Clause or reference</Form.Label>
                                            <Form.Control
                                              value={ruleForm.clauses}
                                              onChange={(event) => setRuleForm({ ...ruleForm, clauses: event.target.value })}
                                              placeholder="Clause 12"
                                            />
                                          </Col>
                                        </Row>
                                        <div>
                                          <Form.Label>Technical key</Form.Label>
                                          <Form.Control
                                            value={ruleForm.key}
                                            onChange={(event) => setRuleForm({ ...ruleForm, key: event.target.value })}
                                            placeholder="Auto-generated if left blank"
                                          />
                                          <Form.Text>Only set this when a system rule needs a fixed key.</Form.Text>
                                        </div>
                                        <div>
                                          <Form.Label>Parameters JSON</Form.Label>
                                          <Form.Control
                                            value={ruleForm.parametersJson}
                                            onChange={(event) => setRuleForm({ ...ruleForm, parametersJson: event.target.value })}
                                            placeholder='Optional, e.g. {"threshold":3}'
                                          />
                                        </div>
                                      </Stack>
                                    </details>
                                  )}

                                  {canManageMasterData ? (
                                    <Button type="submit" disabled={rulesSaving}>
                                      {rulesSaving ? 'Adding...' : 'Add rule'}
                                    </Button>
                                  ) : (
                                    <div className="text-muted">Read-only for your account.</div>
                                  )}
                                </Stack>
                              </Form>
                            )}
                          </div>
                        </Col>

                        <Col md={7}>
                          {visibleComplianceRules.length === 0 ? (
                            <div className="border rounded p-4 text-center text-muted">
                              No rules in this pack yet.
                            </div>
                          ) : (
                            <Table responsive hover className="align-middle mb-0">
                              <thead>
                                <tr>
                                  <th>Rule</th>
                                  <th>Action</th>
                                  <th>Severity</th>
                                  <th></th>
                                </tr>
                              </thead>
                              <tbody>
                                {visibleComplianceRules.map((rule) => (
                                  <tr key={rule.id}>
                                    <td>
                                      <div className="fw-semibold">{rule.name}</div>
                                      <div className="text-muted mim-setup-help-text">
                                        {rule.pattern || rule.clauses || rule.key}
                                      </div>
                                    </td>
                                    <td><Badge bg="light" text="dark">{getRuleActionLabel(getRuleAction(rule))}</Badge></td>
                                    <td>{rule.severity}</td>
                                    <td className="text-end">
                                      {canEditSelectedRulePack && canManageMasterData && (
                                        <Button type="button" size="sm" variant="outline-danger" onClick={() => removeRule(rule)} disabled={rulesSaving}>
                                          Delete
                                        </Button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          )}
                        </Col>
                      </Row>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab.Pane>

          <Tab.Pane eventKey="brand-whitelist">
            <Card>
              <Card.Body>
                <h2 className="h5 mb-1">Brand whitelist</h2>
                <BrandKeywordWhitelist embedded />
              </Card.Body>
            </Card>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      <Modal show={showAttributeModal} onHide={() => setShowAttributeModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Add attributes to {selectedFamilyLabel}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex flex-wrap justify-content-between align-items-end gap-2 mb-3">
            <div>
              <div className="fw-semibold">Master data</div>
              <div className="text-muted mim-setup-help-text">Select one or more attributes, then click Add selected.</div>
            </div>
            <Form.Control
              className="mim-family-search"
              value={attributeSearch}
              onChange={(event) => setAttributeSearch(event.target.value)}
              placeholder="Search master data"
            />
          </div>

          {availableMasterData.length === 0 ? (
            <div className="border rounded p-3 text-muted">No available master-data attributes found.</div>
          ) : (
            <div className="mim-master-attribute-list">
              {availableMasterData.map((term) => (
                <button
                  key={term.id}
                  type="button"
                  className={`mim-master-attribute-item ${selectedMasterTermIds.includes(term.id) ? 'active' : ''}`}
                  onClick={() => toggleSelectedMasterTerm(term.id)}
                  disabled={familySaving}
                >
                  <span className="d-flex align-items-start gap-2">
                    <Form.Check
                      type="checkbox"
                      checked={selectedMasterTermIds.includes(term.id)}
                      onChange={() => toggleSelectedMasterTerm(term.id)}
                      onClick={(event) => event.stopPropagation()}
                      disabled={familySaving}
                    />
                    <span>
                      <span className="fw-semibold">{term.canonicalTerm}</span>
                      <span className="text-muted d-block mim-setup-help-text">{term.category} · {term.dataType}</span>
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="outline-secondary" onClick={() => setShowAttributeModal(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={addSelectedAttributesToFamily} disabled={familySaving || selectedMasterTermIds.length === 0}>
            {familySaving ? 'Adding...' : `Add selected (${selectedMasterTermIds.length})`}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}
